import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../../utils";
import { useModalStack } from "../../../providers/ModalStackProvider";
// Context to pass modal level down to DialogContent
const DialogLevelContext = /*#__PURE__*/ React.createContext(1);
// True for subtrees rendered inside a DialogContent. Read by popover-based
// components (e.g. Combobox) to decide whether to be modal so their
// body-portaled list brings its own react-remove-scroll lock and stays
// scrollable. This is real subtree containment — NOT the app-global open-modal
// count, which would also be true for an unrelated dialog open elsewhere.
const InsideDialogContext = /*#__PURE__*/ React.createContext(false);
/** Whether the current subtree is rendered inside a Dialog's content. */ export function useInsideDialog() {
    return React.useContext(InsideDialogContext);
}
const Dialog = ({ children, open, ...props })=>{
    const modalStack = useModalStack();
    const [modalInfo, setModalInfo] = React.useState(null);
    // Store modalStack in a ref so the cleanup function always has access to unregister
    const modalStackRef = React.useRef(modalStack);
    modalStackRef.current = modalStack;
    // Only depend on `open` - not modalStack (which changes frequently)
    // useLayoutEffect ensures register() runs before the browser paints,
    // so the global overlay and dialog content appear in the same frame
    React.useLayoutEffect(()=>{
        if (open && modalStackRef.current) {
            const info = modalStackRef.current.register();
            setModalInfo(info);
            return ()=>{
                // Use the ref to ensure we call unregister even if modalStack changed
                modalStackRef.current?.unregister(info.id);
                setModalInfo(null);
            };
        }
    }, [
        open
    ]);
    const level = modalInfo?.level ?? 1;
    return /*#__PURE__*/ _jsx(DialogPrimitive.Root, {
        open: open,
        ...props,
        children: /*#__PURE__*/ _jsx(DialogLevelContext.Provider, {
            value: level,
            children: children
        })
    });
};
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;
const DialogContent = /*#__PURE__*/ React.forwardRef(({ className, children, onOpenAutoFocus, onInteractOutside, style, hideAutoClose, ...props }, ref)=>{
    const level = React.useContext(DialogLevelContext);
    const modalStack = useModalStack();
    // Calculate z-index: base 101 + (level - 1) * 10
    const zIndex = 101 + (level - 1) * 10;
    // Calculate progressive scale based on distance from top modal
    // Each level below top gets 5% smaller, minimum 0.85
    // Clamp to >= 0 so a closing top-level modal (where topLevel transiently
    // drops below this still-mounted level) doesn't scale UP via inline transform
    // and step on the Tailwind zoom-out-95 exit keyframe.
    const topLevel = modalStack?.topLevel ?? level;
    const levelsFromTop = Math.max(0, topLevel - level);
    const scale = Math.max(0.85, 1 - levelsFromTop * 0.05);
    return /*#__PURE__*/ _jsx(DialogPortal, {
        children: /*#__PURE__*/ _jsxs(DialogPrimitive.Content, {
            ref: ref,
            className: cn("fixed left-[50%] top-[50%] grid w-full max-w-lg gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg max-h-[90vh] overflow-y-auto transition-transform", className),
            style: {
                zIndex,
                transform: `translate(-50%, -50%) scale(${scale})`,
                ...style
            },
            onOpenAutoFocus: (e)=>{
                // Blur any focused element outside the dialog before auto-focusing
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                // Call the user-provided handler if it exists
                onOpenAutoFocus?.(e);
            },
            onInteractOutside: (e)=>{
                // react-resizable-panels v4 drives resizing from a document-level
                // pointer listener, and Radix's DismissableLayer reads the separator's
                // pointer-down as an outside interaction and dismisses — so dragging a
                // panel divider closes the dialog. Keep it open while the interaction
                // originates on a resize separator.
                const target = e.detail.originalEvent.target;
                if (target instanceof Element && target.closest("[data-separator]")) {
                    e.preventDefault();
                }
                onInteractOutside?.(e);
            },
            ...props,
            children: [
                /*#__PURE__*/ _jsx(InsideDialogContext.Provider, {
                    value: true,
                    children: children
                }),
                !hideAutoClose && /*#__PURE__*/ _jsxs(DialogPrimitive.Close, {
                    className: "absolute right-4 top-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
                    children: [
                        /*#__PURE__*/ _jsx(X, {
                            className: "h-4 w-4"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "sr-only",
                            children: "Close"
                        })
                    ]
                })
            ]
        })
    });
});
DialogContent.displayName = DialogPrimitive.Content.displayName;
const DialogHeader = ({ className, ...props })=>/*#__PURE__*/ _jsx("div", {
        className: cn("flex flex-col space-y-1.5 text-center sm:text-left", className),
        ...props
    });
DialogHeader.displayName = "DialogHeader";
const DialogFooter = ({ className, ...props })=>/*#__PURE__*/ _jsx("div", {
        className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className),
        ...props
    });
DialogFooter.displayName = "DialogFooter";
const DialogTitle = /*#__PURE__*/ React.forwardRef(({ className, ...props }, ref)=>/*#__PURE__*/ _jsx(DialogPrimitive.Title, {
        ref: ref,
        className: cn("text-lg font-semibold leading-none tracking-tight", className),
        ...props
    }));
DialogTitle.displayName = DialogPrimitive.Title.displayName;
const DialogDescription = /*#__PURE__*/ React.forwardRef(({ className, ...props }, ref)=>/*#__PURE__*/ _jsx(DialogPrimitive.Description, {
        ref: ref,
        className: cn("text-sm text-muted-foreground", className),
        ...props
    }));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
export { Dialog, DialogPortal, DialogClose, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription };

//# sourceMappingURL=dialog.js.map